import type React from "react";

import { Avatar, Button, Flex, Tag, Typography } from "antd";

import styles from "./SnapshotHeader.module.scss";

const { Text, Title } = Typography;

interface SnapshotHeaderAction {
	label: string;
	type?: "primary" | "default" | "dashed" | "text" | "link";
	onClick?: () => void;
}

interface SnapshotHeaderMeta {
	/**
	 * Prefer `label` + `value` for consistent layout (optionally with `icon`).
	 * `content` is supported for backward compatibility.
	 */
	icon?: React.ReactNode;
	label?: string;
	value?: React.ReactNode;
	content?: React.ReactNode;
	tooltip?: string;
}

interface SnapshotHeaderProps {
	avatar?: React.ReactNode;
	title: string;
	statusLabel?: string;
	statusColor?: string;
	meta?: SnapshotHeaderMeta[];
	actions?: SnapshotHeaderAction[];
	className?: string;
	avatarClassName?: string;
	infoClassName?: string;
	nameClassName?: string;
	metaClassName?: string;
	actionsClassName?: string;
}

export const SnapshotHeader = ({
	avatar,
	title,
	statusLabel,
	statusColor = "default",
	meta = [],
	actions = [],
	className,
	avatarClassName,
	infoClassName,
	nameClassName,
	metaClassName,
	actionsClassName,
}: SnapshotHeaderProps) => (
	<Flex
		align="center"
		justify="space-between"
		gap="var(--spacing-xxxlarge)"
		className={className}
	>
		<Flex align="center" gap="var(--spacing-xxxlarge)" flex={1}>
			{typeof avatar === "string" ? (
				<Avatar size={56} className={avatarClassName}>
					{avatar}
				</Avatar>
			) : (
				avatar
			)}

			<Flex vertical className={infoClassName} flex={1}>
				<Title level={4} className={nameClassName}>
					{title}
				</Title>
				<Flex className={`${styles.metaRow} ${metaClassName ?? ""}`}>
					{statusLabel && (
						<span className={styles.statusTag}>
							<Tag color={statusColor}>{statusLabel}</Tag>
						</span>
					)}
					{meta.map((m, idx) => (
						<span key={idx} className={styles.metaChip}>
							{m.icon && (
								<span className={styles.metaIcon}>
									{m.icon}
								</span>
							)}

							{m.content ? (
								<Text
									className={styles.metaText}
									ellipsis={{ tooltip: m.tooltip ?? true }}
								>
									{m.content}
								</Text>
							) : (
								<Text
									className={styles.metaText}
									ellipsis={{ tooltip: m.tooltip ?? true }}
								>
									{m.label && (
										<span className={styles.metaLabel}>
											{m.label}
										</span>
									)}
									{m.label && m.value ? " " : ""}
									{m.value && (
										<span className={styles.metaValue}>
											{m.value}
										</span>
									)}
								</Text>
							)}
						</span>
					))}
				</Flex>
			</Flex>
		</Flex>

		{actions.length > 0 && (
			<Flex className={actionsClassName}>
				{actions.map((action) => (
					<Button
						key={action.label}
						type={action.type}
						onClick={action.onClick}
					>
						{action.label}
					</Button>
				))}
			</Flex>
		)}
	</Flex>
);
